import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import { isNil, omitBy } from 'lodash-es'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import MLoader from '@/utils/modal/MLoader'
import MAlert from '@/utils/modal/MAlert'
import {
  API_URL,
  AUTH_ERROR_CODE,
  AUTH_MEMBERSHIP_ERROR_CODES,
  AUTH_MEMBERSHIP_ERROR_MESSAGE,
  AUTH_MEMBERSHIP_FALLBACK_MESSAGE,
  DEV_API_URL,
} from '@/utils/consts'
import { ApiStorage } from '@/utils/storage/ApiStorage'
import { AuthUtils } from '@/utils/AuthUtils'
import { Utils } from '@/utils/Utils'
import { GRID_COLUMNS_API_PATH } from '@/services/auth/consts'
import { getOktaAccessToken, getOrRenewOktaAccessToken, reauthenticateWithOktaOnce } from '@/utils/okta/oktaAuth'

type MomsApiRequestConfig = AxiosRequestConfig & { noAuth?: boolean; skipOktaReauth?: boolean }

// 로컬 caddy 리버스 프록시(:5000, `npm run caddy:dev`) 경유 여부.
// 이 경우 API 를 동일 출처 상대경로(/api*)로 호출 → caddy 가 X-Moms-Api-Env 헤더 기준으로
// 선택된 dev 환경에 프록시한다. 동일 출처라 브라우저 CORS·preflight 가 없어 일반 크롬에서 그대로 동작.
// (vite 직접 포트 :4000 은 기존 절대주소 동작을 유지 → `npm run chrome` 용)
const isCaddyProxy = Utils.isLocal() && window.location.port === '5000'

export const http = axios.create({
  baseURL: Utils.isProd()
    ? API_URL
    : isCaddyProxy
      ? ''
      : DEV_API_URL[ApiStorage.apiType.get()] || process.env.REACT_APP_API_URL,
  timeout: 100000,
})

http.interceptors.request.use(
  async (config) => {
    // caddy 프록시에 어느 dev 환경으로 보낼지 전달 (동일 출처라 preflight 없음)
    if (isCaddyProxy && config.headers) {
      config.headers['X-Moms-Api-Env'] = ApiStorage.apiType.get()
    }

    // Okta 세션이면 요청 직전 토큰을 갱신(만료 시 refresh) 후 Authorization 최신화 → 4. 자동 리프레시
    if (AuthStorage.authMode.get() === 'okta') {
      const authorization = config.headers.get('Authorization')
      const hadBearerToken = typeof authorization === 'string' && authorization.startsWith('Bearer ')
      const fresh = await getOrRenewOktaAccessToken()
      if (fresh) {
        config.headers.set('Authorization', `Bearer ${fresh}`)
      } else if (hadBearerToken) {
        config.headers.delete('Authorization')
        if ((config as MomsApiRequestConfig).skipOktaReauth !== true) {
          reauthenticateWithOktaOnce().catch(() => undefined)
        }
        throw Object.assign(new Error('Okta access token is unavailable'), {
          code: 'OKTA_ACCESS_TOKEN_UNAVAILABLE',
          config,
        })
      }
    }

    // LOGISPRD-15456 — 개인 컬럼 배치 저장은 화면 뒤에서 나가는 요청이라 로더로 화면을 막지 않는다.
    if (config.method !== 'get' && !['/api/auth/menu', '/api/auth/check'].includes(config.url || '') && !config.url?.startsWith(GRID_COLUMNS_API_PATH)) {
      MLoader.show()
    }

    return config
  },
  function (error) {
    MLoader.hide()
    return Promise.reject(error)
  },
)

// LOGISPRD-9057: 세션 중 멤버십 403(정지/미승인 등) 동시다발 응답이 logout 을 중복 호출하지 않게 하는 가드.
// 가드 수명을 logout 결과에 묶는다 — 성공(redirect)이면 곧 unload 되므로 유지(중복 logout 방지), 실패면 즉시 해제
// (영구 잠금=fail-open 방지). 둘 다 아닌 무응답(행)에만 이 backstop 시간을 보험으로 쓴다.
const MEMBERSHIP_BLOCK_GUARD_MS = 3000
let membershipBlockHandled = false

http.interceptors.response.use(
  (config) => {
    MLoader.hide()
    return config
  },
  function (error) {
    const errorCode = Utils.getApiErrorCode(error)
    const isOktaSession = AuthStorage.authMode.get() === 'okta'
    // skipOktaReauth: 로그인 콜백의 사용자 조회처럼, 401/403 이 "재인증/세션차단"이 아니라 "콜백 단계 조회 실패"인
    // 호출은 여기서 처리하면 안 됨(콜백↔Okta 무한 루프 방지). OktaLoginCallback 이 직접 에러 처리한다.
    const skipOktaReauth = error?.config?.skipOktaReauth === true

    const isUnauthorized = error?.response?.status === 401 || errorCode === AUTH_ERROR_CODE.UNAUTHORIZED

    // Okta 인증은 유효하나 MOMS 멤버십이 막힌 403 (미등록/미승인/정지). BE AuthInterceptor 가 매 요청 fresh
    // 재검증(캐시X, ADR-20260611-01)하므로 로그인 이후 세션 중에도 도달 가능 → 전역 방어가 필요하다.
    const isMembershipBlocked = error?.response?.status === 403 && AUTH_MEMBERSHIP_ERROR_CODES.has(errorCode ?? '')

    if (isUnauthorized) {
      if (isOktaSession && !skipOktaReauth) {
        reauthenticateWithOktaOnce().catch(() => undefined)
      } else if (!isOktaSession) {
        alert('토큰이 유효하지 않습니다.')
        AuthUtils.logout()
      }
    } else if (isMembershipBlocked && isOktaSession && !skipOktaReauth) {
      // 세션 중 권한 상실 → 안내 후 로그아웃. 회원가입 유도는 로그인 진입(콜백) 전용이라 여기선 하지 않는다.
      if (!membershipBlockHandled) {
        membershipBlockHandled = true
        const message = (errorCode && AUTH_MEMBERSHIP_ERROR_MESSAGE[errorCode]) || AUTH_MEMBERSHIP_FALLBACK_MESSAGE
        MAlert.show(message)
        // 성공(redirect): 곧 unload → 가드 유지. 실패(reject): 즉시 해제 → 이후 차단 재처리. 무응답: backstop 해제.
        const releaseGuard = () => {
          membershipBlockHandled = false
        }
        const backstop = setTimeout(releaseGuard, MEMBERSHIP_BLOCK_GUARD_MS)
        Promise.resolve(AuthUtils.logout()).then(undefined, () => {
          clearTimeout(backstop)
          releaseGuard()
        })
      }
    }

    MLoader.hide()
    return Promise.reject(error)
  },
)

const getOptions = (config?: MomsApiRequestConfig) => {
  const { headers, noAuth, ...rest } = config || {}
  // 현재 세션이 Okta 로그인일 때만 Bearer. 그 외(Knox/미로그인)는 기존 token.
  // 토큰 "존재 여부"가 아니라 "로그인 방식"으로 분기 → localStorage 에 남은 Okta 토큰이 Knox 세션을 오염시키지 않음.
  const oktaToken = AuthStorage.authMode.get() === 'okta' ? getOktaAccessToken() : undefined
  const authorization = oktaToken ? `Bearer ${oktaToken}` : `token ${AuthStorage.auth.get()}`

  return {
    ...rest,
    headers: omitBy(
      {
        'Content-Type': 'application/json',
        ...(noAuth ? undefined : { Authorization: authorization }),
        ...headers,
      },
      isNil,
    ),
  } as AxiosRequestConfig
}

export function get<T, R = UnknownMap>(url: string, queryParams?: R, config?: Omit<MomsApiRequestConfig, 'params'>): Promise<T> {
  return new Promise((resolve, reject) => {
    http
      .get<T>(`${url}${Utils.qsStringify<R>(queryParams)}`, getOptions(config))
      .then((result) => {
        if (result.data) {
          resolve(result.data)
        } else {
          reject({ error: { message: 'empty' } })
        }
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export function post<T, R = UnknownMap>(url: string, body: R, config?: MomsApiRequestConfig): Promise<T> {
  return new Promise((resolve, reject) => {
    http
      .post<T>(url, body, getOptions(config))
      .then((result) => {
        resolve(result.data)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export function put<T, R = UnknownMap>(url: string, body: R, config?: MomsApiRequestConfig): Promise<T> {
  return new Promise((resolve, reject) => {
    http
      .put<T>(url, body, getOptions(config))
      .then((result) => {
        if (result.data) {
          resolve(result.data)
        } else {
          reject({ error: { message: 'empty' } })
        }
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export function patch<T, R = UnknownMap>(url: string, body: R, config?: MomsApiRequestConfig): Promise<T> {
  return new Promise((resolve, reject) => {
    http
      .patch<T>(url, body, getOptions(config))
      .then((result) => {
        if (result.data) {
          resolve(result.data)
        } else {
          reject({ error: { message: 'empty' } })
        }
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export function deleteR<T>(url: string, id?: number | string | null, queryParams?: UnknownMap, config?: MomsApiRequestConfig): Promise<T> {
  return new Promise((resolve, reject) => {
    const deleteUrl = !isNil(id) ? `${url}/${id}` : url

    http
      .delete<T>(deleteUrl, getOptions({ params: queryParams || {}, ...config }))
      .then((result) => {
        resolve(result.data)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export function deleteWithBody<T, R = UnknownMap>(url: string, body: R, config?: MomsApiRequestConfig): Promise<T> {
  return new Promise((resolve, reject) => {
    http
      .delete<T>(url, getOptions({ ...config, data: body }))
      .then((result) => {
        resolve(result.data)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export function getExcel(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<Blob>> {
  if (config && config.method === 'post') {
    return new Promise((resolve, reject) => {
      http
        .post(url, config.data, getOptions({ ...config, responseType: 'blob' }))
        .then((result) => {
          if (result.data) {
            resolve(result)
          } else {
            reject({ error: { message: 'empty' } })
          }
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  return new Promise((resolve, reject) => {
    http
      .get(url, getOptions({ ...config, responseType: 'blob' }))
      .then((result) => {
        if (result.data) {
          resolve(result)
        } else {
          reject({ error: { message: 'empty' } })
        }
      })
      .catch((error) => {
        reject(error)
      })
  })
}

export default { get, post, put, patch, delete: deleteR, deleteWithBody }
